/**
 * DashboardPage — `/` route entry.
 *
 * Fetches members + jobs; gates on combined loading/error; dispatches to
 * one of three state components. Each state component is rendered as a
 * sibling — no shared layout container, no animation.
 *
 * State derivation lives in useDashboardState.ts (pure, testable in isolation).
 */

import { Navigate } from 'react-router-dom';
import { useMembers } from '@/features/members/hooks';
import { useJobsList } from '@/features/jobs/hooks';
import { useAuthStore } from '@/stores/auth';
import { Alert } from '@/components/Alert/Alert';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import { deriveDashboardState } from './useDashboardState';
import { EmptyDashboard } from './states/EmptyDashboard';
import { OnboardingDashboard } from './states/OnboardingDashboard';
import { PopulatedDashboard } from './states/PopulatedDashboard';
import styles from './Dashboard.module.css';

function DashboardSkeleton() {
  return (
    <div className={styles.skeleton} data-testid="dashboard-skeleton" aria-busy="true">
      <div className={styles.skeletonGreeting} />
      <div className={styles.skeletonTileRow}>
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
        <div className={styles.skeletonTile} />
      </div>
      <div className={styles.skeletonTwoCol}>
        <div className={styles.skeletonCol} />
        <div className={styles.skeletonCol} />
      </div>
    </div>
  );
}

export function DashboardPage() {
  const members = useMembers();
  const jobs = useJobsList();
  const firstName = useAuthStore((s) => s.user?.first_name ?? '');
  const welcomedAt = useAuthStore((s) => s.tenantUser?.welcomedAt);

  if (welcomedAt === null) return <Navigate to="/welcome" replace />;

  if (members.isLoading || jobs.isLoading) {
    return <DashboardSkeleton />;
  }

  if (members.isError || jobs.isError || !members.data || !jobs.data) {
    return (
      <Alert variant="error" title={t("We couldn't load your dashboard.")}>
        {t('Please try again. If the problem persists, refresh the page.')}
        <div className={styles.errorActions}>
          <Button
            variant="secondary"
            onClick={() => {
              void members.refetch();
              void jobs.refetch();
            }}
          >
            {t('Retry')}
          </Button>
        </div>
      </Alert>
    );
  }

  const state = deriveDashboardState({
    memberCount: members.data.items.length,
    jobCount: jobs.data.items.length,
  });

  switch (state) {
    case 'empty':
      return <EmptyDashboard firstName={firstName} />;
    case 'onboarding':
      return <OnboardingDashboard firstName={firstName} members={members.data.items} />;
    case 'populated':
      return (
        <PopulatedDashboard
          firstName={firstName}
          members={members.data.items}
          jobs={jobs.data.items}
        />
      );
  }
}
